import { QueryKeys } from "@/constants/Query";
import { useApi } from "@/hooks/useApi";
import axiosInstance from "@/lib/axios";
import { RequestGroup, ServiceRequest } from "@curai/js-fhir-tools";

interface LabsOrderParams {
  patientId: string;
  encounterId: string;
  enabled?: boolean;
}

export enum LabOrderPaymentStatus {
  UNPAID = "unpaid",
  PAYMENT_REQUESTED = "payment_requested",
  PAID = "paid",
  NOT_REQUIRED = "not_required",
}

export interface LabOrderEntryActionSummary {
  readyForSubmission: boolean;
  orderHasAoes: boolean;
  aoeSatisfied: boolean;
  compendiumSatisfied: boolean;
  canCancel: boolean;
  canCancelWithoutPaymentCheck: boolean;
  canAddTests: boolean;
}

export interface LabOrderEntryStatusSummary {
  paymentStatus: LabOrderPaymentStatus;
  submittedToLab: boolean;
}

export interface LabOrderByEncounterItem {
  encounterId: string;
  requestGroup: RequestGroup;
  tests: ServiceRequest[];
  statusSummary: LabOrderEntryStatusSummary;
  actionSummary: LabOrderEntryActionSummary;
}

interface LabOrdersByEncounterResponse {
  orders: LabOrderByEncounterItem[];
}

export const useLabOrdersByEncounter = ({
  patientId,
  encounterId,
  enabled,
}: LabsOrderParams) => {
  return useApi<LabOrdersByEncounterResponse>({
    queryOptions: {
      enabled: Boolean((enabled ?? true) && encounterId),
      queryFn: async () => {
        const response = await axiosInstance.get<LabOrdersByEncounterResponse>(
          `/labs/order/v2/encounter/${encounterId}`,
        );

        return response.data;
      },
      queryKey: QueryKeys.Patient.Chart.Labs.DEFAULT_V2({
        patientId,
        encounterId,
      }),
      select: (data) => ({
        orders: data.orders.map((labOrder) => ({
          ...labOrder,
          requestGroup: new RequestGroup(labOrder.requestGroup),
          tests: labOrder.tests.map((test) => new ServiceRequest(test)),
        })),
      }),
    },
  });
};
